/**
 * The seed example graph.
 *
 * This is the builder's own money plumbing, generalised onto the graph: salary
 * lands in the Starling hub, bills are ring-fenced into HSBC and paid out from
 * there, the Capital One card is cleared, a little goes to a holiday pot, a
 * European supplier is paid, and a one-off car service lands this month.
 *
 * It ships with v1 as a ready-made demo and doubles as a realistic end-to-end
 * fixture for the engine. All amounts are integer pence.
 */
import { pence } from "./money.js";
/** The currency the seed graph's safe-to-spend figure is reported in. */
export const SEED_PRIMARY_CURRENCY = "GBP";
/** The month the seed graph is framed around, as YYYY-MM. */
export const SEED_MONTH = "2026-07";
const seedNodes = [
    {
        id: "income-salary",
        type: "income",
        name: "Salary",
        currency: "GBP",
        positionX: -360,
        positionY: 0,
        meta: { amount: pence(240000), payDay: 28 },
    },
    {
        id: "acct-starling",
        type: "account",
        name: "Starling",
        currency: "GBP",
        positionX: 0,
        positionY: 0,
        meta: { balance: pence(0), role: "income_hub" },
    },
    {
        id: "acct-hsbc",
        type: "account",
        name: "HSBC Bills",
        currency: "GBP",
        positionX: 360,
        positionY: -160,
        meta: { balance: pence(0), role: "bills" },
    },
    {
        id: "card-capital-one",
        type: "card",
        name: "Capital One",
        currency: "GBP",
        positionX: 360,
        positionY: 40,
        meta: {
            balance: pence(45000),
            creditLimit: pence(300000),
            aprBasisPoints: 2990,
            minPayment: pence(2500),
            statementBalance: pence(45000),
            dueDay: 15,
            payment: pence(45000),
        },
    },
    {
        id: "pot-holiday",
        type: "pot",
        name: "Holiday Pot",
        currency: "GBP",
        positionX: 360,
        positionY: 200,
        meta: { balance: pence(0), target: pence(100000), topUp: pence(20000) },
    },
    {
        id: "ext-eu-supplier",
        type: "external",
        name: "EU Supplier",
        currency: "EUR",
        positionX: 360,
        positionY: 340,
        meta: { amount: pence(15000) },
    },
    {
        id: "oneoff-car-service",
        type: "oneoff",
        name: "Car Service",
        currency: "GBP",
        positionX: 0,
        positionY: 240,
        meta: { amount: pence(30000), dueDate: "2026-07-12" },
    },
    // Individual bills, paid out of the ring-fenced HSBC account.
    {
        id: "bill-rent",
        type: "bill",
        name: "Rent",
        currency: "GBP",
        positionX: 720,
        positionY: -260,
        meta: { amount: pence(95000), frequency: "monthly", dueDay: 1 },
    },
    {
        id: "bill-council-tax",
        type: "bill",
        name: "Council Tax",
        currency: "GBP",
        positionX: 720,
        positionY: -160,
        meta: { amount: pence(18000), frequency: "monthly", dueDay: 1 },
    },
    {
        id: "bill-broadband",
        type: "bill",
        name: "Broadband",
        currency: "GBP",
        positionX: 720,
        positionY: -60,
        meta: { amount: pence(4000), frequency: "monthly", dueDay: 3 },
    },
    {
        id: "bill-mobile",
        type: "bill",
        name: "Mobile",
        currency: "GBP",
        positionX: 720,
        positionY: 40,
        meta: { amount: pence(3000), frequency: "monthly", dueDay: 6 },
    },
];
const seedFlows = [
    {
        id: "flow-salary-in",
        sourceNodeId: "income-salary",
        // The salary sweeps in full, so editing the income amount drives it.
        targetNodeId: "acct-starling",
        amountType: "remainder",
        amountValue: 0,
        schedule: "on_payday",
        label: "Salary into Starling",
    },
    {
        id: "flow-ring-fence-bills",
        sourceNodeId: "acct-starling",
        targetNodeId: "acct-hsbc",
        amountType: "fixed",
        amountValue: 120000,
        schedule: "on_payday",
        label: "Ring-fence bills to HSBC",
    },
    {
        id: "flow-pay-card",
        sourceNodeId: "acct-starling",
        targetNodeId: "card-capital-one",
        amountType: "fixed",
        amountValue: 45000,
        schedule: "on_payday",
        label: "Clear Capital One statement",
    },
    {
        id: "flow-pay-supplier",
        sourceNodeId: "acct-starling",
        targetNodeId: "ext-eu-supplier",
        amountType: "fixed",
        amountValue: 15000,
        schedule: "monthly",
        label: "Pay EU supplier",
    },
    {
        id: "flow-save-holiday",
        sourceNodeId: "acct-starling",
        targetNodeId: "pot-holiday",
        amountType: "fixed",
        amountValue: 20000,
        schedule: "on_payday",
        label: "Top up holiday pot",
    },
    {
        id: "flow-car-service",
        sourceNodeId: "acct-starling",
        targetNodeId: "oneoff-car-service",
        amountType: "fixed",
        amountValue: 30000,
        schedule: "one_off",
        label: "Pay for the car service",
    },
    {
        id: "flow-bill-rent",
        sourceNodeId: "acct-hsbc",
        targetNodeId: "bill-rent",
        amountType: "fixed",
        amountValue: 95000,
        schedule: "monthly",
        label: "Rent",
    },
    {
        id: "flow-bill-council-tax",
        sourceNodeId: "acct-hsbc",
        targetNodeId: "bill-council-tax",
        amountType: "fixed",
        amountValue: 18000,
        schedule: "monthly",
        label: "Council Tax",
    },
    {
        id: "flow-bill-broadband",
        sourceNodeId: "acct-hsbc",
        targetNodeId: "bill-broadband",
        amountType: "fixed",
        amountValue: 4000,
        schedule: "monthly",
        label: "Broadband",
    },
    {
        id: "flow-bill-mobile",
        sourceNodeId: "acct-hsbc",
        targetNodeId: "bill-mobile",
        amountType: "fixed",
        amountValue: 3000,
        schedule: "monthly",
        label: "Mobile",
    },
];
/** Deep clone of plain data, preserving the static type. The seed graph is pure
 * data (numbers and strings), so a JSON round-trip is safe and deterministic. */
function clone(value) {
    return JSON.parse(JSON.stringify(value));
}
/** The complete seed graph: nodes plus flows. Returned fresh each call so
 * callers (such as the API seeder) can mutate their own copy safely. */
export function buildSeedGraph() {
    return { nodes: clone(seedNodes), flows: clone(seedFlows) };
}
//# sourceMappingURL=seed.js.map